import { staticFile } from '@common/utils'
import { CheckCircleFilled } from '@components/common/icons/custom-icons'
import Tooltip from '@components/common/tooltip'
import RescheduleClassButton from '@components/reschedule-course-class/reschedule-class-button'
import { useSessionContext } from '@contexts/session'
import {
  COURSE_MODULES_IMAGE_PATH,
  LESSON_CONTAINER_CLASS,
  LESSON_STATE,
  MODULE_STATE,
} from '@lib/constants/course-modules'
import { LessonType, ModuleStateType } from '@lib/types/course-modules'
import {
  formatTimeByTimezone,
  SYSTEM_TIMEZONE,
  TIME_FORMAT_TZ_ONLY_DATE,
  TIME_FORMAT_TZ_ONLY_TIME,
} from '@lib/utils/date.utils'
import ongoingClassDot from '@public/animations/ongoing-class-dot.json'
import upcomingClassDot from '@public/animations/upcoming-class-dot.json'
import classNames from 'classnames'
import Lottie from 'lottie-react'
import Link from 'next/link'
import { Info } from 'react-feather'

type CompletedLessonCheckListItemProps = {
  isComplete?: boolean
  title: string
}

const CompletedLessonChecklistItem = ({
  isComplete = false,
  title,
}: CompletedLessonCheckListItemProps) => (
  <div className="flex items-center space-x-2">
    {isComplete ? (
      <CheckCircleFilled className="w-6 h-6 text-green-200" />
    ) : (
      <div className="flex items-center justify-center w-5 h-5 text-white border rounded-full font-600 bg-orange">
        !
      </div>
    )}
    <div className="flex space-x-1">
      <span>{title}:</span>
      <span>{isComplete ? 'Completed' : 'Not completed'}</span>
    </div>
  </div>
)

type CalendarIconProps = {
  selectedDate: string
  isInRescheduleModal?: boolean
}

export const CalendarIcon = ({
  selectedDate,
  isInRescheduleModal,
}: CalendarIconProps) => {
  const { timezone } = useSessionContext()
  const studentTimezone = timezone || SYSTEM_TIMEZONE
  return (
    <div
      className="flex flex-col items-start mt-3"
      style={{ minWidth: '9rem', maxWidth: '10rem' }}
    >
      <div className="relative flex flex-col items-center px-3 py-4 border-4 rounded-lg font-600 text-grey border-grey-300 text-sm lg:text-base text-center">
        <div className="absolute top-0 flex items-center justify-center w-full -mt-3 space-x-16">
          <div className="h-5 border-4 rounded-lg border-grey-300" />
          <div className="h-5 border-4 rounded-lg border-grey-300" />
        </div>
        {!isInRescheduleModal && <p>{selectedDate}</p>}
        {isInRescheduleModal && (
          <>
            <p>
              {formatTimeByTimezone(
                selectedDate,
                studentTimezone,
                TIME_FORMAT_TZ_ONLY_TIME
              )}
            </p>
            <p>
              {formatTimeByTimezone(
                selectedDate,
                studentTimezone,
                TIME_FORMAT_TZ_ONLY_DATE
              )}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

type Props = {
  lesson: LessonType
  lessonNumber: number
  moduleState: ModuleStateType
  isOnDashboard?: boolean
  isOnCoursePage?: boolean
}

const LessonCard = ({
  lesson: {
    startTime,
    startInUserTimezone,
    title,
    state,
    outline,
    teacher,
    activitiesCompleted,
    projectsCompleted,
    quizzesCompleted,
    totalActivities,
    totalProjects,
    totalQuizzes,
    batchId,
    classroomId,
    gamificationPoints,
    isGroupClass,
  },
  lessonNumber,
  moduleState,
  isOnDashboard = false,
  isOnCoursePage,
}: Props) => {
  const { student } = useSessionContext()

  const relationshipManager = student?.relationshipManager
  const studentId = student?.id

  return (
    <div
      className={`${
        isOnCoursePage ? 'py-4' : 'py-6'
      } flex space-x-3 border-t border-grey-400 ${LESSON_CONTAINER_CLASS}`}
    >
      {!isOnCoursePage && (
        <div className="lg:block hidden">
          <CalendarIcon selectedDate={startInUserTimezone} />
        </div>
      )}
      <div className="flex flex-col items-start space-y-3 leading-tight">
        {!isOnCoursePage && (
          <div className="lg:hidden">
            <CalendarIcon selectedDate={startInUserTimezone} />
          </div>
        )}
        <div className="flex lg:flex-row flex-col lg:items-center">
          <p className="text-lg font-600 text-grey">
            Lesson {lessonNumber}: {title}
          </p>
          {state === LESSON_STATE.COMPLETED && (
            <div className="relative flex items-center lg:ml-8">
              <div className="lg:absolute w-3 h-3 lg:-ml-5 mr-2 lg:mr-0 rounded-full bg-orange" />
              <p className="text-orange">Completed</p>
            </div>
          )}
          {state === LESSON_STATE.NEXT_UPCOMING &&
            (isOnDashboard || moduleState === MODULE_STATE.ONGOING) && (
              <div className="relative flex items-center lg:ml-8">
                <Lottie
                  animationData={upcomingClassDot}
                  className="lg:absolute block w-10 h-10 lg:-ml-8"
                />
                <p className="text-blue-200">Upcoming</p>
              </div>
            )}
          {state === LESSON_STATE.ONGOING && (
            <div className="relative flex items-center lg:ml-8">
              <Lottie
                animationData={ongoingClassDot}
                className="lg:absolute block w-5 h-5 lg:-ml-6"
              />
              <p className="text-green-200">Ongoing</p>
            </div>
          )}
        </div>
        <p>{outline}</p>
        {teacher && gamificationPoints && (
          <div className="hidden lg:flex items-center pt-2 space-x-8">
            <div className="flex items-center space-x-1">
              <img
                src={staticFile(
                  `${COURSE_MODULES_IMAGE_PATH}module-card/teacher.png`
                )}
                className="w-6 h-6 mr-1"
              />
              <p>
                <span className="font-500 text-grey">Teacher:</span>{' '}
                {teacher.name}
              </p>
            </div>
            <div className="flex items-center space-x-1">
              <img
                src={staticFile(
                  `${COURSE_MODULES_IMAGE_PATH}module-card/speedometer.png`
                )}
                className="w-6 h-6 mr-1"
              />
              <p>
                <span className="font-500 text-grey">Points:</span> +
                {gamificationPoints}
              </p>
              <Tooltip
                content={`You will get ${gamificationPoints} points on completing this class`}
                showOnHover
              >
                <Info className="cursor-pointer text-grey-700" size={18} />
              </Tooltip>
            </div>
          </div>
        )}
        {state === LESSON_STATE.COMPLETED && (
          <div className="hidden lg:flex flex-col space-y-5">
            <div className="flex space-x-5">
              {!!totalActivities && (
                <CompletedLessonChecklistItem
                  isComplete={activitiesCompleted >= totalActivities}
                  title="Activities"
                />
              )}
              {!!totalProjects && (
                <CompletedLessonChecklistItem
                  isComplete={projectsCompleted >= totalProjects}
                  title="Projects"
                />
              )}
              {!!totalQuizzes && (
                <CompletedLessonChecklistItem
                  isComplete={quizzesCompleted >= totalQuizzes}
                  title="Quiz"
                />
              )}
            </div>
            <Link href={`/student/dashboard/classes/${classroomId}/`}>
              <a className="text-orange hover:underline font-600">
                View class details
              </a>
            </Link>
          </div>
        )}
        {!isOnCoursePage &&
          (isOnDashboard || moduleState === MODULE_STATE.ONGOING) &&
          (state === LESSON_STATE.ONGOING ||
            state === LESSON_STATE.NEXT_UPCOMING) && (
            <div className="flex pt-2 space-x-5">
              <Link href={`/batches/${batchId}/classroom/`}>
                <a
                  target="_blank"
                  className={classNames('btn', {
                    'btn-orange': state === LESSON_STATE.ONGOING,
                    'text-orange hover:bg-orange-200 active:bg-orange-300':
                      state !== LESSON_STATE.ONGOING,
                  })}
                >
                  Join class
                </a>
              </Link>
              {state === LESSON_STATE.NEXT_UPCOMING && !isGroupClass && (
                <RescheduleClassButton
                  lessonNumber={lessonNumber}
                  lessonTitle={title}
                  teacherCalendarId={teacher.calendarId}
                  batchId={batchId}
                  classroomId={classroomId}
                  classStartTime={startTime}
                  relationshipManager={relationshipManager}
                  studentId={studentId}
                  isGroupClass={isGroupClass}
                />
              )}
            </div>
          )}
      </div>
    </div>
  )
}
export default LessonCard
